import { createNewChart, setIndicatorRenderKey } from "./chartState";
import { updateIndicatorDisplayName } from "../../utils/indicatorDisplayName.js";
import {
  fetchChartData,
  loadMoreBars,
  cancelSeamReconcile,
  rebaseXExtentsForReplace,
  resolveReplaceAnchor,
} from "./chartDataProcessor";
import { findBarByTimestamp } from "../../chartLib/lib/utils/ChartDataUtil";
import logger from "../../utils/logger";
import marketdata from "../../data/Services/marketdata.jsx";
import useRatingStore from "../ratingStore.js";
import {
  validateIndicatorDependencies,
  wouldCreateCycle,
  canSafelyRemove,
  extractSourceIndicatorId,
  topologicalSort,
  findDependents,
} from "../../utils/indicatorDependencyValidator.js";
import { isPhaseEnabled } from "../../config/featureFlags.js";
import { globalToast } from "../../components/ToastProvider.jsx";
import { confirmDialog } from "primereact/confirmdialog";

/**
 * Migrate a single drawing's coordinates from saved dates to current indices.
 * This fixes Issue #104: drawings maintain position even when new bars are added.
 *
 * @param {Object} drawing - The drawing object with date fields (startDate, endDate, x1Date, etc.)
 * @param {Array} chartData - The current chart data
 * @returns {Object} - The drawing with updated x coordinates
 */
const migrateDrawingCoordinates = (drawing, chartData) => {
  if (!drawing || !chartData?.length) return drawing;

  const migrated = { ...drawing };

  // Use array index as the x-coordinate since raw data from API doesn't have idx.index
  // The array position corresponds to the discontinuous scale index
  const getBarIndex = (bar) => {
    if (!bar) return undefined;
    // First try idx.index (if data has been processed by chart)
    if (bar.idx?.index !== undefined) return bar.idx.index;
    // Fallback: find array position (for raw API data)
    return chartData.indexOf(bar);
  };

  // Migrate start/end format (TrendLine, Segment, Line, Ray, Rectangle, Ellipse, etc.)
  if (drawing.startDate && Array.isArray(drawing.start)) {
    const bar = findBarByTimestamp(chartData, drawing.startDate);
    const barIndex = getBarIndex(bar);
    if (bar && barIndex !== undefined && barIndex >= 0) {
      migrated.start = [barIndex, drawing.start[1]];
    }
  }
  if (drawing.endDate && Array.isArray(drawing.end)) {
    const bar = findBarByTimestamp(chartData, drawing.endDate);
    const barIndex = getBarIndex(bar);
    if (bar && barIndex !== undefined && barIndex >= 0) {
      migrated.end = [barIndex, drawing.end[1]];
    }
  }

  // Migrate x1/x2/x3 format (Fibonacci tools, channels, etc.)
  if (drawing.x1Date !== undefined) {
    const bar = findBarByTimestamp(chartData, drawing.x1Date);
    const barIndex = getBarIndex(bar);
    if (bar && barIndex !== undefined && barIndex >= 0) migrated.x1 = barIndex;
  }
  if (drawing.x2Date !== undefined) {
    const bar = findBarByTimestamp(chartData, drawing.x2Date);
    const barIndex = getBarIndex(bar);
    if (bar && barIndex !== undefined && barIndex >= 0) migrated.x2 = barIndex;
  }
  if (drawing.x3Date !== undefined) {
    const bar = findBarByTimestamp(chartData, drawing.x3Date);
    const barIndex = getBarIndex(bar);
    if (bar && barIndex !== undefined && barIndex >= 0) migrated.x3 = barIndex;
  }

  // Migrate position format (InteractiveText, Callout)
  if (drawing.positionDate !== undefined && Array.isArray(drawing.position)) {
    const bar = findBarByTimestamp(chartData, drawing.positionDate);
    const barIndex = getBarIndex(bar);
    if (bar && barIndex !== undefined && barIndex >= 0) {
      migrated.position = [barIndex, drawing.position[1]];
    }
  }

  // Migrate xValue format (VerticalLine)
  if (drawing.xValueDate !== undefined) {
    const bar = findBarByTimestamp(chartData, drawing.xValueDate);
    const barIndex = getBarIndex(bar);
    if (bar && barIndex !== undefined && barIndex >= 0)
      migrated.xValue = barIndex;
  }

  return migrated;
};

/**
 * Migrate all interactive objects' coordinates for a chart.
 * Called after chart data is loaded to restore drawing positions.
 *
 * @param {Object} interactiveObjects - The chart's interactive objects (keyed by chartId or chartId_sectionId)
 * @param {Array} chartData - The loaded chart data
 * @returns {Object} - The migrated interactive objects
 */
const migrateInteractiveCoordinates = (interactiveObjects, chartData) => {
  if (!interactiveObjects || !chartData?.length) return interactiveObjects;

  const migrated = {};

  for (const [key, objects] of Object.entries(interactiveObjects)) {
    if (Array.isArray(objects)) {
      // Check if any object in this array has date fields (needs migration)
      const hasDates = objects.some(
        (obj) =>
          obj?.startDate ||
          obj?.endDate ||
          obj?.x1Date ||
          obj?.x2Date ||
          obj?.x3Date ||
          obj?.positionDate ||
          obj?.xValueDate,
      );

      if (hasDates) {
        migrated[key] = objects.map((obj) =>
          migrateDrawingCoordinates(obj, chartData),
        );
      } else {
        // No dates to migrate, keep as-is (backward compatibility for old saves)
        migrated[key] = objects;
      }
    } else {
      migrated[key] = objects;
    }
  }

  return migrated;
};

// Chart management actions
export const chartActions = (set, get) => ({
  setCharts: (charts) => set({ charts }),

  addNewChart: async () => {
    const newChart = createNewChart();
    set((state) => ({
      charts: [...state.charts, newChart],
      // Initialize chartData entry for the new chart
      chartData: {
        ...state.chartData,
        [newChart.id]: { data: [], dataVersion: 0 },
      },
      selectedTab: state.charts.length,
    }));
    await fetchChartData(newChart, get().updateChartConfig, get().setData);
    return newChart.id;
  },

  removeChart: (id) => {
    // Unregister quote subscription before removing chart
    const chart = get().charts.find((c) => c.id === id);
    if (chart?.symbol) {
      marketdata.unregisterQuote(chart.symbol, `chart-${id}`);
    }
    cancelSeamReconcile(id);

    set((state) => {
      const charts = state.charts.filter((chart) => chart.id !== id);
      // Clean up chartData entry for the removed chart
      const { [id]: removed, ...remainingChartData } = state.chartData;
      let selectedTab = state.selectedTab;
      if (charts.length === 0) {
        selectedTab = null;
      } else if (state.charts[state.selectedTab]?.id === id) {
        selectedTab = 0;
      }
      return { charts, chartData: remainingChartData, selectedTab };
    });
  },

  setSelectedTab: (tabIdx) =>
    set((state) => ({
      selectedTab: tabIdx,
      focusedChartId: state.charts[tabIdx]?.id || null,
    })),

  setFocusedChart: (chartId) => set({ focusedChartId: chartId }),

  updateChartGroup: (chartId, group) =>
    set((state) => ({
      charts: state.charts.map((chart) =>
        chart.id === chartId ? { ...chart, group } : chart,
      ),
    })),

  /**
   * Change chart symbol (used by group linking protocol)
   * This is the receiver callback for single-symbol panels.
   * @param {string} chartId - Chart ID to update
   * @param {string} symbol - New symbol to set
   */
  changeChartSymbol: async (chartId, symbol, opts = {}) => {
    const chart = get().getChart(chartId);
    if (!chart || chart.symbol === symbol) return;

    // Engagement trigger — real symbol change (after the no-op guard). Skipped for
    // programmatic callers (group-link propagation/receiver) via opts.skipEngagement.
    if (!opts.skipEngagement) useRatingStore.getState().registerEngagement();

    // Unregister old symbol before changing
    marketdata.unregisterQuote(chart.symbol, `chart-${chartId}`);

    // Update symbol and reset xExtents
    get().updateChartConfig(chartId, { symbol, xExtents: null });

    // Fetch new data (will register new symbol)
    await get().fetchChartData(chartId);
  },

  updateChartConfig: (id, newConfig) =>
    set((state) => {
      // Check if we need to increment dataVersion (stored in chartData now)
      const currentChart = state.charts.find((c) => c.id === id);
      const renkoConfigChanged =
        newConfig.renkoConfig &&
        JSON.stringify(newConfig.renkoConfig) !==
          JSON.stringify(currentChart?.renkoConfig);
      const pointFigureConfigChanged =
        newConfig.pointFigureConfig &&
        JSON.stringify(newConfig.pointFigureConfig) !==
          JSON.stringify(currentChart?.pointFigureConfig);

      const shouldIncrementVersion =
        renkoConfigChanged || pointFigureConfigChanged;

      const chartDataUpdate = shouldIncrementVersion
        ? {
            chartData: {
              ...state.chartData,
              [id]: {
                ...state.chartData[id],
                dataVersion: (state.chartData[id]?.dataVersion || 0) + 1,
              },
            },
          }
        : {};

      return {
        charts: state.charts.map((chart) =>
          chart.id === id ? { ...chart, ...newConfig } : chart,
        ),
        ...chartDataUpdate,
      };
    }),

  updateIndicators: (id, newIndicators) =>
    set((state) => ({
      charts: state.charts.map((chart) =>
        chart.id === id ? { ...chart, indicators: newIndicators } : chart,
      ),
    })),

  // Data is now stored in chartData map (separate from charts metadata)
  // This prevents Workspace re-renders on data updates
  setData: (chartId, data) =>
    set((state) => {
      const nextChartData = {
        ...state.chartData,
        [chartId]: { data, dataVersion: 0, pendingInvalidation: true },
      };

      // Re-anchor the viewport to the new last bar. Replacing the array (boot
      // restore, gap auto-reload, reconnect) re-bases absolute indices: the live
      // array grew with real-time appends while getBars refetches a fixed window,
      // so a saved/live xExtents right edge would otherwise sit past the last bar
      // and the chart would render panned left. See rebaseXExtentsForReplace.
      const chart = state.charts.find((c) => c.id === chartId);
      let nextCharts = state.charts;
      if (chart) {
        const oldData = state.chartData[chartId]?.data;
        // Anchor = last index the stored xExtents is valid for: live array
        // (in-session refetch), persisted anchor (current-code desktop), or — for
        // a legacy desktop with no anchor — the saved right edge so it snaps
        // flush-right on load. See resolveReplaceAnchor.
        const oldNewest = resolveReplaceAnchor(
          oldData,
          chart.xExtents,
          chart.xExtentsLastIndex,
        );
        const rebased = rebaseXExtentsForReplace(
          chart.xExtents,
          oldNewest,
          data.length,
        );
        if (rebased) {
          nextCharts = state.charts.map((c) =>
            c.id === chartId ? { ...c, ...rebased } : c,
          );
        }
      }

      return nextCharts === state.charts
        ? { chartData: nextChartData }
        : { chartData: nextChartData, charts: nextCharts };
    }),

  // Clear the invalidation flag after the chart has rendered fresh data
  clearInvalidation: (chartId) =>
    set((state) => {
      const current = state.chartData[chartId];
      if (!current || !current.pendingInvalidation) return state;
      return {
        chartData: {
          ...state.chartData,
          [chartId]: { ...current, pendingInvalidation: false },
        },
      };
    }),

  // Nudge: force re-render by incrementing dataVersion without changing data.
  // Used after setData to ensure the rendering pipeline picks up fresh historical data,
  // especially when no real-time quotes are flowing (market closed, low liquidity).
  nudgeChart: (chartId) =>
    set((state) => {
      const current = state.chartData[chartId];
      if (!current) return state;
      return {
        chartData: {
          ...state.chartData,
          [chartId]: {
            ...current,
            dataVersion: (current.dataVersion || 0) + 1,
          },
        },
      };
    }),

  updateVisibleBars: (chartId, visibleBars) => {
    set((state) => ({
      charts: state.charts.map((chart) =>
        chart.id === chartId ? { ...chart, visibleBars } : chart,
      ),
    }));
  },

  updateXExtents: (chartId, xExtents) => {
    set((state) => ({
      charts: state.charts.map((chart) =>
        chart.id === chartId ? { ...chart, xExtents } : chart,
      ),
    }));
  },

  // Issue #105: Store hidden chart state for auto-scroll on remount
  // Called when chart becomes hidden (tab switch) to track bar count
  setChartHiddenState: (chartId, lastVisibleBarIndex, wasLastBarVisible) => {
    set((state) => ({
      charts: state.charts.map((chart) =>
        chart.id === chartId
          ? { ...chart, lastVisibleBarIndex, wasLastBarVisible }
          : chart,
      ),
    }));
  },

  // Issue #105: Clear hidden chart state after handling
  clearChartHiddenState: (chartId) => {
    set((state) => ({
      charts: state.charts.map((chart) =>
        chart.id === chartId
          ? { ...chart, lastVisibleBarIndex: null, wasLastBarVisible: null }
          : chart,
      ),
    }));
  },

  // Prepend older bars to beginning of data array and adjust xExtents
  prependBars: (chartId, olderBars) => {
    set((state) => {
      const chart = state.charts.find((c) => c.id === chartId);
      if (!chart) return state;

      const currentChartData = state.chartData[chartId];
      if (!currentChartData) return state;

      // Prepend bars to beginning
      const newData = [...olderBars, ...currentChartData.data];

      // Adjust xExtents to preserve scroll position
      let newXExtents = chart.xExtents;
      if (newXExtents && olderBars.length > 0) {
        const [start, end] = newXExtents;
        newXExtents = [start + olderBars.length, end + olderBars.length];
        console.log(
          `[prependBars] Adjusted xExtents from [${start}, ${end}] to [${newXExtents[0]}, ${newXExtents[1]}] (shifted by ${olderBars.length})`,
        );
      }

      return {
        // Update xExtents in charts array (metadata change)
        charts: state.charts.map((c) =>
          c.id === chartId ? { ...c, xExtents: newXExtents } : c,
        ),
        // Update data and dataVersion in chartData map
        chartData: {
          ...state.chartData,
          [chartId]: {
            data: newData,
            dataVersion: currentChartData.dataVersion + 1,
          },
        },
      };
    });
  },

  // Load more historical bars incrementally
  loadMoreHistoricalBars: async (chartId) => {
    const { charts, chartData, updateChartConfig, prependBars } = get();
    const chart = charts.find((c) => c.id === chartId);
    const currentChartData = chartData[chartId];

    if (!chart) {
      console.warn("[loadMoreHistoricalBars] Chart not found:", chartId);
      return;
    }

    if (chart.isLoadingMore) {
      console.warn("[loadMoreHistoricalBars] Already loading more bars");
      return;
    }

    if (!chart.hasMoreData) {
      console.log("[loadMoreHistoricalBars] No more historical data available");
      return;
    }

    // Set loading state
    updateChartConfig(chartId, { isLoadingMore: true });

    try {
      // Pass data from chartData map to loadMoreBars
      const olderBars = await loadMoreBars(chart, currentChartData?.data, 2000);

      if (olderBars.length > 0) {
        prependBars(chartId, olderBars);
        console.log(
          `[loadMoreHistoricalBars] Successfully loaded ${olderBars.length} older bars`,
        );
      } else {
        console.log(
          "[loadMoreHistoricalBars] No bars returned - may be end of data or temporary issue",
        );
        // Only disable after multiple consecutive failures to avoid false negatives
        // For now, keep hasMoreData true to allow retry on next scroll attempt
        console.log(
          "[loadMoreHistoricalBars] Keeping hasMoreData=true to allow retry",
        );
      }
    } catch (error) {
      console.error("[loadMoreHistoricalBars] Error:", error);
      // Don't set hasMoreData=false on errors - allow retry
    } finally {
      updateChartConfig(chartId, { isLoadingMore: false });
    }
  },

  // Indicator management actions
  addIndicatorToChart: (chartId, indicator, opts = {}) => {
    set((state) => {
      const chartIndex = state.charts.findIndex(
        (chart) => chart.id === chartId,
      );
      if (chartIndex === -1) return state;

      const updatedCharts = [...state.charts];
      const chart = { ...updatedCharts[chartIndex] };
      const updatedIndicator = setIndicatorRenderKey({ ...indicator });

      // Validate dependencies if VALIDATION_ONLY phase is enabled
      const validationEnabled = isPhaseEnabled(
        "SERIES_AS_SOURCE",
        "VALIDATION_ONLY",
      );
      if (validationEnabled) {
        const existingIndicators = chart.indicators || [];
        const validation = validateIndicatorDependencies(
          existingIndicators,
          updatedIndicator,
        );

        if (!validation.valid) {
          // Log error and show to user
          logger.system.error("Não é possível adicionar indicador", {
            reason: validation.errors[0],
            errors: validation.errors,
          });
          console.error("Indicator validation failed:", validation.errors);
          return state; // ABORT: Don't add indicator
        }

        // Log warnings to console but don't show to user (removed "Aviso de dependência")
        if (validation.warnings.length > 0) {
          console.warn("Indicator validation warnings:", validation.warnings);
        }
      }

      chart.indicators = chart.indicators
        ? [...chart.indicators, updatedIndicator]
        : [updatedIndicator];

      updatedCharts[chartIndex] = chart;
      return {
        ...state,
        charts: updatedCharts,
      };
    });
    // Engagement trigger — OUTSIDE the set updater (I6). Skipped for programmatic
    // callers (template apply) via opts.skipEngagement.
    if (!opts.skipEngagement) useRatingStore.getState().registerEngagement();
  },

  updateIndicatorConfig: (chartId, indicatorId, config, appearanceConfig) => {
    set((state) => {
      const chartIndex = state.charts.findIndex(
        (chart) => chart.id === chartId,
      );
      if (chartIndex === -1) return state;

      const updatedCharts = [...state.charts];
      const chart = { ...updatedCharts[chartIndex] };

      // Find the indicator being updated
      const indicatorToUpdate = chart.indicators.find(
        (ind) => ind.id === indicatorId,
      );
      if (!indicatorToUpdate) return state;

      // Create the updated indicator
      const updatedIndicator = {
        ...indicatorToUpdate,
        config: { ...indicatorToUpdate.config, ...config },
        appearanceConfig: {
          ...indicatorToUpdate.appearanceConfig,
          ...appearanceConfig,
          yAccessor: undefined, // force yAccessor to undefined
        },
      };
      setIndicatorRenderKey(updatedIndicator);

      // Build updated indicator list for displayName generation
      const allIndicatorsIncludingUpdated = [
        ...chart.indicators.filter((ind) => ind.id !== indicatorId),
        updatedIndicator,
      ];

      // PHASE 3: Update displayNames using topological sort to avoid recursive issues
      // Process in dependency order: sources first, then dependents
      // This ensures RSI displayName is finalized before EMA reads it
      const sortedIndicators = topologicalSort(allIndicatorsIncludingUpdated);

      // Update displayNames in dependency order
      sortedIndicators.forEach((indicator) => {
        // Pass the CURRENT state of all indicators (with updated displayNames)
        updateIndicatorDisplayName(indicator, sortedIndicators);
      });

      // Validate dependencies if VALIDATION_ONLY phase is enabled
      const validationEnabled = isPhaseEnabled(
        "SERIES_AS_SOURCE",
        "VALIDATION_ONLY",
      );
      if (validationEnabled) {
        // Check if sourcePath changed (potential circular reference)
        const sourcePathChanged =
          config.sourcePath !== undefined &&
          config.sourcePath !== indicatorToUpdate.config?.sourcePath;

        if (sourcePathChanged) {
          const otherIndicators = chart.indicators.filter(
            (ind) => ind.id !== indicatorId,
          );
          const cycleCheck = wouldCreateCycle(
            otherIndicators,
            updatedIndicator,
          );

          if (cycleCheck.wouldCycle) {
            // Log error and show to user
            const cyclePath = cycleCheck.cyclePath.join(" → ");
            logger.system.error("Referência circular detectada", {
              cycle: cyclePath,
            });
            console.error("Circular reference detected:", cycleCheck.cyclePath);

            // Show toast notification
            globalToast.showError(
              `Não é possível criar dependência circular: ${cyclePath}`,
              "Referência circular",
            );

            return state; // ABORT: Don't update indicator
          }

          // Also run full validation
          const validation = validateIndicatorDependencies(
            otherIndicators,
            updatedIndicator,
          );
          if (!validation.valid) {
            logger.system.error("Não é possível atualizar indicador", {
              reason: validation.errors[0],
              errors: validation.errors,
            });
            console.error("Indicator validation failed:", validation.errors);

            // Show toast notification with first error
            globalToast.showError(validation.errors[0], "Validação falhou");

            return state; // ABORT: Don't update indicator
          }

          // Log warnings to console but don't show to user (removed "Aviso de dependência")
          if (validation.warnings.length > 0) {
            console.warn("Indicator validation warnings:", validation.warnings);
          }
        }
      }

      // Update the indicator
      chart.indicators = chart.indicators.map((indicator) => {
        if (indicator.id === indicatorId) {
          return updatedIndicator;
        }
        return indicator;
      });

      updatedCharts[chartIndex] = chart;
      return { ...state, charts: updatedCharts };
    });
  },

  removeIndicatorFromChart: (chartId, indicatorId) => {
    set((state) => {
      const chartIndex = state.charts.findIndex(
        (chart) => chart.id === chartId,
      );
      if (chartIndex === -1) return state;

      const updatedCharts = [...state.charts];
      const chart = { ...updatedCharts[chartIndex] };

      // Find indicator being removed
      const indicatorToRemove = chart.indicators.find(
        (ind) => ind.id === indicatorId,
      );
      if (!indicatorToRemove) return state;

      // Check if this indicator is being used for bar coloring
      const barColorProvider = chart.barColorProvider;
      if (barColorProvider && barColorProvider.startsWith("indicator:")) {
        // Extract indicator type from provider key (e.g., 'indicator:rsi' -> 'rsi')
        const requiredIndicatorType = barColorProvider.replace(
          "indicator:",
          "",
        );

        // Check if the indicator being removed is required by the bar color provider
        if (indicatorToRemove.key === requiredIndicatorType) {
          const indicatorDisplayName =
            indicatorToRemove.displayName || indicatorToRemove.key;

          // Show warning dialog
          confirmDialog({
            message: `O indicador "${indicatorDisplayName}" está sendo usado para coloração das barras. A coloração será alterada para "Tradicional" se você removê-lo.`,
            header: "Indicador em uso",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Remover mesmo assim",
            rejectLabel: "Cancelar",
            acceptClassName: "p-button-danger",
            accept: () => {
              // User confirmed - reset to traditional coloring first
              const currentState = get();
              const currentChartIndex = currentState.charts.findIndex(
                (c) => c.id === chartId,
              );
              if (currentChartIndex !== -1) {
                const chartsUpdate = [...currentState.charts];
                chartsUpdate[currentChartIndex] = {
                  ...chartsUpdate[currentChartIndex],
                  barColorProvider: "traditional",
                };
                set({ charts: chartsUpdate });
              }

              // Then proceed with indicator removal
              // Call removeIndicatorFromChart again (it will skip bar color check since we changed it)
              get().removeIndicatorFromChart(chartId, indicatorId);
            },
            reject: () => {
              // User cancelled - do nothing
              logger.system.info("Remoção de indicador cancelada pelo usuário");
            },
          });

          return state; // Return current state (dialog is async)
        }
      }

      // Validate removal if VALIDATION_ONLY phase is enabled
      const validationEnabled = isPhaseEnabled(
        "SERIES_AS_SOURCE",
        "VALIDATION_ONLY",
      );
      if (validationEnabled) {
        // Use immutable ID for dependency checking
        const removalCheck = canSafelyRemove(chart.indicators, indicatorId);

        if (!removalCheck.canRemove) {
          // Get dependent indicators with their display names
          const dependentIndicators = removalCheck.dependents
            .map((dependentId) =>
              chart.indicators.find((ind) => ind.id === dependentId),
            )
            .filter(Boolean);

          const dependentDisplayNames = dependentIndicators
            .map((dep) => dep.displayName || dep.renderKey)
            .join(", ");

          const indicatorDisplayName =
            indicatorToRemove.displayName ||
            indicatorToRemove.renderKey ||
            indicatorId;

          // Build list of all indicators that will be deleted (including nested dependents)
          const getAllDependents = (
            targetId,
            indicators,
            visited = new Set(),
          ) => {
            if (visited.has(targetId)) return [];
            visited.add(targetId);

            const directDependents = findDependents(indicators, targetId);
            const allDeps = [...directDependents];

            directDependents.forEach((dep) => {
              const nestedDeps = getAllDependents(dep.id, indicators, visited);
              allDeps.push(...nestedDeps);
            });

            return allDeps;
          };

          const allDependents = getAllDependents(indicatorId, chart.indicators);
          const allDependentsNames = allDependents
            .map((dep) => dep.displayName || dep.renderKey)
            .join(", ");

          // Build message with list of dependents
          const dependentsList = allDependents
            .map((dep) => `• ${dep.displayName || dep.renderKey}`)
            .join("\n");

          const dialogMessage = `${indicatorDisplayName} está sendo usado por:\n\n${dependentsList}\n\nDeseja remover todos esses indicadores juntos?`;

          // Show confirmation dialog
          confirmDialog({
            message: dialogMessage,
            header: "Remover indicadores dependentes?",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Remover todos",
            rejectLabel: "Cancelar",
            acceptClassName: "p-button-danger",
            accept: () => {
              // User confirmed - cascade delete all dependents
              const idsToRemove = new Set([
                indicatorId,
                ...allDependents.map((d) => d.id),
              ]);

              // Remove all indicators in the dependency tree
              const updatedIndicators = chart.indicators.filter(
                (indicator) => !idsToRemove.has(indicator.id),
              );

              chart.indicators = updatedIndicators;
              chart.sectionHeights = null; // Reset layout

              updatedCharts[chartIndex] = chart;
              set({ charts: updatedCharts });

              // Show success toast
              globalToast.showSuccess(
                `${idsToRemove.size} indicador(es) removido(s): ${indicatorDisplayName}${allDependents.length > 0 ? `, ${allDependentsNames}` : ""}`,
                "Indicadores removidos",
              );

              logger.system.info("Indicadores removidos em cascata", {
                removed: Array.from(idsToRemove).join(", "),
              });
            },
            reject: () => {
              // User cancelled - do nothing
              logger.system.info("Remoção cancelada pelo usuário");
            },
          });

          return state; // Return current state (dialog is async)
        }
      }

      // No dependents - remove directly
      chart.indicators = chart.indicators.filter(
        (indicator) => indicator.id !== indicatorId,
      );

      // Reset section heights to recalculate layout after indicator removal
      chart.sectionHeights = null;

      updatedCharts[chartIndex] = chart;
      return { ...state, charts: updatedCharts };
    });
  },

  // Workspace management actions
  loadWorkspace: async (charts, selectedTab = 0) => {
    console.log(
      "[LoadWorkspace] Loading workspace with",
      charts.length,
      "charts",
    );
    console.log(
      "[LoadWorkspace] Charts details:",
      charts.map((c) => ({
        symbol: c.symbol,
        periodicity: c.periodicity,
        indicators: c.indicators?.length || 0,
        interactiveObjects: Object.keys(c.interactiveObjects || {}).length,
      })),
    );

    // Initialize chartData entries for all charts
    const initialChartData = {};
    charts.forEach((chart) => {
      initialChartData[chart.id] = { data: [], dataVersion: 0 };
    });

    // First load the charts configuration
    set((state) => ({
      ...state,
      charts: charts.map((chart) => ({
        ...chart,
        isLoading: false,
        error: null,
        // Note: data is now in chartData map, not in chart object
      })),
      chartData: initialChartData,
      selectedTab,
      activeInteractiveTools: {},
    }));

    console.log("[LoadWorkspace] Charts configuration set in store");

    // Then fetch data for each loaded chart
    const { updateChartConfig, setData, updateInteractiveObjects } = get();
    const chartsToLoad = charts.filter((c) => c.symbol && c.periodicity);
    const totalCharts = chartsToLoad.length;
    let chartIndex = 0;

    set({
      workspaceLoadProgress: {
        current: 0,
        total: totalCharts,
        symbol: chartsToLoad[0]?.symbol || "",
      },
    });

    for (const chart of charts) {
      if (chart.symbol && chart.periodicity) {
        try {
          console.log(
            `[LoadWorkspace] Fetching data for: ${chart.symbol} (${chart.periodicity})`,
          );
          console.log(
            `[LoadWorkspace]   Indicators: ${chart.indicators?.length || 0}`,
          );
          console.log(
            `[LoadWorkspace]   Interactive objects: ${Object.keys(chart.interactiveObjects || {}).length}`,
          );

          // Data loading logged in console only - UI logger message removed to reduce noise
          await fetchChartData(chart, updateChartConfig, setData);
          console.log(`[LoadWorkspace] ✅ Data fetched for ${chart.symbol}`);

          // Issue #104: Migrate interactive coordinates using saved dates
          // After data loads, restore drawing positions using timestamps instead of indices
          const loadedChartData = get().chartData[chart.id];
          if (loadedChartData?.data?.length && chart.interactiveObjects) {
            // Check if there are any interactive objects to migrate
            const hasInteractiveObjects = Object.values(
              chart.interactiveObjects,
            ).some(
              (sectionObj) =>
                sectionObj &&
                typeof sectionObj === "object" &&
                Object.values(sectionObj).some(
                  (arr) => Array.isArray(arr) && arr.length > 0,
                ),
            );

            if (hasInteractiveObjects) {
              console.log(
                `[LoadWorkspace] Migrating interactive coordinates for ${chart.symbol}`,
              );

              // Migrate each section's interactive objects
              // Structure: { "chartId" or "chartId_sectionId": { trendLine: [...], fibRetracement: [...] } }
              for (const [sectionKey, sectionObjects] of Object.entries(
                chart.interactiveObjects,
              )) {
                if (sectionObjects && typeof sectionObjects === "object") {
                  // Extract section ID if present (format: chartId_sectionId or just chartId)
                  const parts = sectionKey.split("_");
                  const sectionId =
                    parts.length > 1
                      ? parseInt(parts[parts.length - 1], 10)
                      : null;

                  // Migrate each object type in the section (trendLine, fibRetracement, etc.)
                  const migratedSection = migrateInteractiveCoordinates(
                    sectionObjects,
                    loadedChartData.data,
                  );

                  // Update each object type in the store
                  for (const [objectType, migratedItems] of Object.entries(
                    migratedSection,
                  )) {
                    if (
                      Array.isArray(migratedItems) &&
                      migratedItems.length > 0
                    ) {
                      updateInteractiveObjects(
                        chart.id,
                        objectType,
                        migratedItems,
                        sectionId,
                      );
                    }
                  }
                }
              }
              console.log(
                `[LoadWorkspace] ✅ Interactive coordinates migrated for ${chart.symbol}`,
              );
            }
          }
        } catch (error) {
          console.error(
            `[LoadWorkspace] ❌ Failed to fetch data for chart ${chart.symbol}:`,
            error,
          );

          // Log de erro para o usuário
          logger.data.error(`Falha ao carregar dados do gráfico`, {
            symbol: chart.symbol,
            periodicity: chart.periodicity,
            error: error.message,
          });
          // Continue with other charts even if one fails
        }
        chartIndex++;
        set({
          workspaceLoadProgress: {
            current: chartIndex,
            total: totalCharts,
            symbol: chart.symbol,
          },
        });
      }
    }

    // Brief pause so the user sees 100% before the overlay disappears
    await new Promise((r) => setTimeout(r, 400));
    set({ workspaceLoadProgress: null });
    console.log("[LoadWorkspace] ✅ Workspace load complete");
  },

  clearWorkspace: () => {
    // Unregister all chart quote subscriptions before clearing
    const { charts } = get();
    for (const chart of charts) {
      if (chart?.symbol) {
        marketdata.unregisterQuote(chart.symbol, `chart-${chart.id}`);
      }
      cancelSeamReconcile(chart.id);
    }

    set((state) => ({
      ...state,
      charts: [],
      chartData: {}, // Clear all chart data
      selectedTab: 0,
      activeInteractiveTools: {},
    }));
  },
});
